export type AgentContactRecord = {
  id?: number | string
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  name?: string | null
  title?: string | null
  phoneNumber?: string | null
  email?: string | null
  fbMessengerId?: string | null
  telegramId?: string | null
  instagramId?: string | null
  weChatId?: string | null
  lineId?: string | null
  waId?: string | null
  [key: string]: unknown
}

const MIN_PHONE_DIGITS = 6

type PrimitiveLike = string | number | bigint | null | undefined

const identifierFields: (keyof AgentContactRecord)[] = [
  'phoneNumber',
  'email',
  'fbMessengerId',
  'telegramId',
  'instagramId',
  'weChatId',
  'lineId',
  'waId',
]

const toTrimmedString = (value: PrimitiveLike): string | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return value.toString()
  }

  return null
}

export const sanitizeContactLookupValue = (
  value: PrimitiveLike
): string | null => {
  return toTrimmedString(value)
}

export const normalizeForComparison = (value: PrimitiveLike): string | null => {
  const str = toTrimmedString(value)
  if (!str) {
    return null
  }

  const numericCandidate = str.replace(/\D+/g, '')
  if (numericCandidate.length >= MIN_PHONE_DIGITS) {
    return numericCandidate
  }

  return str.toLowerCase()
}

export const extractContactIdentifiers = (
  contact: AgentContactRecord
): string[] => {
  return identifierFields
    .map(field => sanitizeContactLookupValue(contact[field] as PrimitiveLike))
    .filter((value): value is string => Boolean(value))
}

export const contactMatchesIdentifier = (
  contact: AgentContactRecord,
  identifier: string
): boolean => {
  const target = normalizeForComparison(identifier)
  if (!target) {
    return false
  }

  return extractContactIdentifiers(contact).some(value => {
    const normalized = normalizeForComparison(value)
    return normalized === target
  })
}

export const getContactDisplayName = (
  contact: AgentContactRecord
): string | undefined => {
  const fullName = sanitizeContactLookupValue(contact.fullName)
  if (fullName) {
    return fullName
  }

  const name = sanitizeContactLookupValue(contact.name)
  if (name) {
    return name
  }

  const firstName = sanitizeContactLookupValue(contact.firstName)
  const lastName = sanitizeContactLookupValue(contact.lastName)
  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  }
  if (firstName) {
    return firstName
  }
  if (lastName) {
    return lastName
  }

  const title = sanitizeContactLookupValue(contact.title)
  if (title) {
    return title
  }

  return undefined
}

export interface SessionContactDescriptor {
  platform?: string | null
  contactAddress?: string | null
  platformId?: string | null
  userId?: string | number | null
  identifiers?: Array<string | number | null | undefined>
}

export const deriveSessionContactIdentifiers = (
  descriptor: SessionContactDescriptor
): string[] => {
  const candidateMap = new Map<string, string>()

  const addCandidate = (candidate: PrimitiveLike) => {
    const raw = sanitizeContactLookupValue(candidate)
    if (!raw) {
      return
    }

    const normalized = normalizeForComparison(raw)
    if (!normalized) {
      return
    }

    if (!candidateMap.has(normalized)) {
      candidateMap.set(normalized, raw)
    }
  }

  addCandidate(descriptor.contactAddress)
  addCandidate(descriptor.platformId)
  addCandidate(descriptor.userId)

  if (Array.isArray(descriptor.identifiers)) {
    descriptor.identifiers.forEach(addCandidate)
  }

  return Array.from(candidateMap.values())
}
